/**
 * Every user-visible string in the module, in one place — the en-US catalogue.
 *
 * Copy follows the prototype verbatim wherever a requirement does not state a
 * different outcome; the strings a spec oracle quotes exactly are marked with
 * their requirement id.
 *
 * PLACEHOLDERS
 * ------------
 * A message that needs a value in it carries a `{name}` placeholder and is
 * rendered through `i18n/translate`'s `interpolate` or `plural`. It is NOT
 * assembled with a template literal at the call site: `` `(${count} reviews)` ``
 * fixes English word order, English pluralisation and the position of the
 * parentheses inside a component, where no translator can reach any of them.
 * The four messages under `preview.product` are the ones that used to be built
 * that way.
 *
 * Adding a locale means adding a sibling catalogue of the same shape — the
 * placeholders and plural categories travel with the message, so nothing in a
 * component has to change.
 */

export const STRINGS = {
  environment: {
    /**
     * Shown by both route entries when no environment is selected in the
     * search bar — cards live on a channel, so there is nothing to load yet.
     */
    noSelection: 'Select an environment to work with Product Cards.',
  },
  listing: {
    pageTitle: 'Product Cards',
    searchPlaceholder: 'Search cards…',
    newCardButton: '+ New Card',
    columns: {
      /** R16's four headers, plus authorship (operator, 2026-08-19). */
      name: 'Card Name',
      referencedIn: 'Referenced In',
      createdBy: 'Created By',
      createdAt: 'Created At',
      updatedAt: 'Updated At',
    },
    /**
     * R21 — the compact DS `EmptyState` in the table body when a search matches
     * nothing. No Create action there: the fix is the search box the user is in.
     */
    noSearchResults: 'No cards match your search.',
    noSearchResultsHint: 'Try a different card name, or clear the search.',
    /**
     * The page-level DS `EmptyState` when the Environment has no cards at all —
     * title names what is absent, description says what a card is for, and the
     * action IS "+ New Card" (was one sentence telling the user to go find it).
     */
    emptyTitle: 'No product cards yet',
    emptyDescription: 'Create a card to choose which product fields display, and how they are laid out.',
    noReferences: '—',
    /** The row kebab's accessible name (Guide's "More options"); the row's name follows it. */
    moreActions: 'More options',
    /** The Delete item inside the row menu, and the delete dialog it opens. */
    deleteAction: 'Delete card',
    /** The rows-per-page Dropdown's accessible name (it carries no visible label, as Guide's). */
    rowsPerPage: 'Rows per page',
    loading: 'Loading product cards…',
  },
  newCardDialog: {
    title: 'Create a new card',
    description: "Give the card a name. You'll set up its display next.",
    nameLabel: 'Card name',
    namePlaceholder: 'e.g. Search Results, Featured, Compare',
    cancel: 'Cancel',
    confirm: 'Create',
    /** Shown on the confirm button while the POST is in flight. */
    creating: 'Creating…',
    /**
     * The fallback under the name box when a create is refused for a reason the
     * server did not attribute to an input. A refusal that DOES name one — the
     * duplicate-name rule — shows the server's own sentence instead, because it
     * names the offending card and this one cannot.
     */
    createFailed: 'The card could not be created. Please try again.',
  },
  deleteDialog: {
    /**
     * R24 — the confirmation names the card (in the title, Guide-style). Only
     * the consequence sentence is authored here: the title template and the
     * Cancel/Delete labels belong to the shared DeleteProjectModal this module
     * reuses (operator decision 2026-08-18).
     */
    warning:
      'This will permanently delete the card and all its display configuration. This can’t be undone.',
    /** Shown on the confirm button while the DELETE request is in flight. */
    deleting: 'Deleting…',
  },
  blockedDeleteDialog: {
    /** R25 — blocking dialog, dismiss only. */
    title: 'Can’t delete — card is in use',
    /**
     * ONE sentence, rendered through `i18n`'s `interpolateNodes` — `{cardName}`
     * is the emphasised name and `{applications}` the badge list, both nodes.
     *
     * It was three siblings in a `flex-col` until 2026-09-29: the name, the
     * bare verb phrase and the badges each got their own line, so the dialog
     * read as a broken list rather than a sentence. Reuniting them also puts
     * the word order and the full stop back where a translator can move them.
     */
    body: '{cardName} is currently being used by {applications}.',
    guidanceSingle: 'Remove this card from this application before it can be deleted.',
    guidanceMultiple: 'Remove this card from each application before it can be deleted.',
    dismiss: 'Close',
  },
  editor: {
    back: 'Back to Product Cards',
    rename: 'Rename card',
    renameLabel: 'Card name',
    /** A rename verb, deliberately not a persist verb — autosave owns the
     *  writing and the module presents no such control anywhere (R8). */
    renameConfirm: 'Rename',
    renameCancel: 'Cancel rename',
    notFound: 'That product card no longer exists.',
    backToListing: 'Back to Product Cards',
    loading: 'Loading card…',
    saveFailed: 'The last change could not be saved.',
    /**
     * The save indicator (R8, R45).
     *
     * There is no Save control in this module by design, which means these
     * strings are the ONLY evidence a user has that their work is safe. Before
     * they existed the feature showed nothing at all on success — "saved" and
     * "quietly failing to save" looked exactly alike.
     *
     * The wording states an outcome ("All changes saved"), not a mechanism
     * ("autosave on"): the user wants to know about their work, not about ours.
     */
    saveStatus: {
      saving: 'Saving…',
      saved: 'All changes saved',
      savedAt: 'All changes saved at {time}',
      error: 'Not saved',
    },
    saveRetry: 'Try saving again',
    /** Announced politely, so it does not interrupt typing. */
    saveRegionLabel: 'Save status',
  },
  /**
   * The editor body's chrome: the two edge controls that collapse and expand the
   * side columns (R75, R76).
   *
   * Each name states what the control toggles, not which way it will move — the
   * collapsed/expanded state is carried by `aria-expanded`, so the name is stable
   * in both states and a screen reader announces the state itself (NFR A1, A2).
   */
  chrome: {
    /** R75 — the configuration pane's edge control. */
    togglePane: 'Toggle the configuration pane',
    /** R76 — the properties panel's edge control. */
    togglePanel: 'Toggle the properties panel',
  },
  /**
   * The configuration pane — the editor's left column (R59) — and the copy of
   * the properties column that opens beside the preview (R63). Named `pane`
   * rather than `rail` since the round that moved it: it is no longer a rail.
   */
  pane: {
    /**
     * The inspector's heading while nothing is selected (operator, 2026-08-15).
     * "Card properties" rather than "Card configuration": it is the same noun the
     * element inspector uses for the selected block, so the column reads as one
     * inspector whose subject changes, not two different panels.
     */
    cardPropertiesTitle: 'Card properties',
    /** Tells the user what fills the rest of the inspector. */
    cardPropertiesHint: 'Select an element on the card to edit its properties.',
    /** R61 — the Card orientation section heading. */
    orientationTitle: 'Card orientation',
    orientationVertical: 'Vertical',
    orientationHorizontal: 'Horizontal',
    /** R61 — the footer checkbox's label. */
    footerCheckbox: 'Add a footer',
    /** R62 — the second section heading. */
    fieldsTitle: 'Fields',
    /** R62 — the field search placeholder is quoted by the oracle. */
    fieldSearchPlaceholder: 'Search fields…',
    noFieldMatches: 'No fields match your search.',
    reorderHandle: 'Reorder field',
    /** R42 — the chevron that opens a field's properties panel. */
    openProperties: 'Open properties',
    /** R42 — the panel's back control closes the properties column; the field
     *  list never left, so "back to fields" would misdescribe it. */
    closeProperties: 'Close properties',
    /** R63 — the panel's remove control; R57 also unticks the field. */
    removeFromCard: 'Remove from card',
    /** R63, R66 — the Label section of a field's properties panel. */
    labelSectionTitle: 'Label',
    /** R43 (amended) — the input's caption, the prototype's own wording. */
    displayLabel: 'Display label (blank = none)',
    /** R43 (amended) — the placeholder is `e.g. ` + the element's panel name. */
    displayLabelPlaceholderPrefix: 'e.g. ',
    /**
     * Superseded by R43's amendment (the placeholder is now per-element, above).
     * Kept only so the round-3 assertion that quotes it still compiles until the
     * test round reconciles it — nothing renders this string.
     */
    displayLabelPlaceholder: 'Leave blank to hide the label',
    alignment: 'Alignment',
    alignmentLeft: 'Left',
    alignmentCenter: 'Center',
    alignmentRight: 'Right',
    imageFit: 'Image display',
    imageFitFit: 'Fit',
    imageFitFill: 'Fill',
    imageFitStretch: 'Stretch',
    /** R71 — the Description style section and its three options. */
    descriptionStyle: 'Description style',
    descriptionStylePartly: 'Clamp 2 lines',
    descriptionStyleFull: 'Full',
    descriptionStyleCollapsed: 'Collapsed',
    /** R69 — the Pricing section and its one checkbox. */
    pricing: 'Pricing',
    showUom: 'Show unit of measure (UOM)',
    /** The per-element price strikethrough (14021). */
    strikethrough: 'Strikethrough',
    /** R70 — the Button section for the Primary CTA. */
    button: 'Button',
    ctaLabel: 'Button label',
    ctaLabelPlaceholder: 'Add to Cart',
    ctaShowText: 'Show text',
    ctaShowIcon: 'Show cart icon',
    ctaFullWidth: 'Full card width',
    /**
     * R70 pins these as `Button width — <n>%` and `Button height — <n>px`. The
     * number is a placeholder rather than a template literal at the call site,
     * so a locale can move it, change the dash, or drop the unit — the en-US
     * output is byte-for-byte what R70 asks for either way.
     */
    ctaWidth: 'Button width — {width}%',
    ctaHeight: 'Button height — {height}px',
    /** R67 — the Custom styling section. */
    customStyling: 'Custom styling',
    /** The CSS input has no visible caption in the prototype; this names it. */
    customCssLabel: 'Custom CSS for',
    /** R67 — the four-line placeholder, quoted by the oracle. */
    customCssPlaceholder:
      '/* CSS for this element only */\ncolor: #D60027;\nfont-size: 18px;\nfont-weight: 700;',
    /**
     * R67 — rendered as `<n> chars · applied live to preview`. One message with
     * the count in it: "chars" is a plural form in its own right, and gluing a
     * number onto the front of a fragment cannot express that.
     */
    customCssCounter: {
      one: '{count} char · applied live to preview',
      other: '{count} chars · applied live to preview',
    },
  },
  preview: {
    title: 'Preview',
    /** R54 — exact copy. */
    hint: 'Drag and drop to adjust the items on the card.',
    /** R48 — the footer's placeholder while it holds nothing. */
    footerEmpty: 'Footer · drag items here',
    /** R48 — what the footer's drop target reads during a drag. */
    footerDropTarget: 'Drop in footer',
    footerDisabled: 'Footer · enable “Add a footer” to use this region',
    deleteBlock: 'Remove from card',
    selectBlock: 'Select block',
    imagePlaceholder: 'Product image',
    dropHere: 'Drop here',
    /** R71 — the description block's collapsed form. */
    descriptionCollapsed: 'Show description',

    /**
     * Copy rendered *inside* a preview block, from the sample product's values.
     *
     * Every one of these was an English literal in `FieldBlockContent.tsx` —
     * `'Price per unit '`, `'Discontinued'`, `` `(${n} reviews)` ``,
     * `` `#${n} Best Seller` `` — so they were invisible to the "all strings in
     * one place" rule the rest of the module follows, and untranslatable.
     */
    product: {
      /** `{price}` already carries its own currency symbol from `formatMoney`. */
      pricePerUnit: 'Price per unit {price}',
      /** The separator between a display label and its value. */
      labelledValue: '{label}: {value}',
      /** Appended to a price when the card shows unit of measure (R69). */
      uomSuffix: ' / {unit}',
      discontinued: 'Discontinued',
      notDiscontinued: 'Not discontinued',
      preferred: 'Preferred',
      newArrival: 'New',
      sponsored: 'Sponsored',
      bestSeller: '#{rank} Best Seller',
      /**
       * CLDR plural categories, not a singular/plural pair. `Intl.PluralRules`
       * picks the form — English needs two, Polish three, Arabic six — so a
       * `count === 1` check at the call site would be wrong everywhere but here.
       * `other` is the one category every locale defines and is therefore the
       * only required member.
       */
      reviewCount: {
        one: '({count} review)',
        other: '({count} reviews)',
      },
    },
  },
} as const;
